import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { useNavigation, useTheme, useRoute } from '@react-navigation/native';
import { Icon } from '@components';
import { Theme } from '@theme';
import { getCommonStyles } from '@utils';
import ContactsListEdit from '../components/ContactsListEdit';
import { useScreen, useGetColorLabelById, useUpdateColorLabel } from '@hooks';
import CloseButton from '../components/CloseButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLanguage } from '@context';

type EditRelationalColorProps = {
  relationalColorId?: string;
  onBack?: () => void;
};

export const EditRelationalColor: React.FC<EditRelationalColorProps> = ({ relationalColorId: propRelationalColorId, onBack }) => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const theme = useTheme();
  const { t } = useLanguage();
  const {isMobile, isAndroid} = useScreen();
  const styles = getStyle(theme);
  const commonStyles = getCommonStyles(theme);
  
  const { relationalColorId: routeRelationalColorId } = route.params || {};
  const relationalColorId = propRelationalColorId || routeRelationalColorId;
  const { data: relationalColor, isFetched } = useGetColorLabelById(relationalColorId);
  const { mutate: updateColorLabel } = useUpdateColorLabel();
  
  const [name, setName] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [hasModified, setHasModified] = useState(false);
  
  useEffect(() => {
    setHasModified(name !== relationalColor?.name || selectedContactIds !== relationalColor?.contactAccountIds);
  }, [name, selectedContactIds]);

  useEffect(() => {
    if (relationalColor) {
      setName(relationalColor.name);
      setSelectedContactIds(relationalColor.contactAccountIds);
    } else {
      setName('');
      setSelectedContactIds([]);
    }
  }, [relationalColor, relationalColorId]);

  if (!relationalColor) {
    return (
      <SafeAreaView style={styles.container}>
        {isMobile && <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()}>
            <Icon name="arrowBack" size={25} color={theme.colors.black} />
          </TouchableOpacity>
        </View>}
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('relationalColorsScreen.notFound')}</Text>
        </View>
      </SafeAreaView>
    );
  }

  const handleBack = () => {
    if(onBack) {
      onBack();
    } else {
      navigation.goBack();
    }
  };

  const handleSave = () => {
    if (name.trim()) {
      updateColorLabel({
        id: relationalColor.id,
        name,
        color: relationalColor.color,
        contactAccountIds: selectedContactIds
      });
      handleBack();
      setHasModified(false);
    }
  };

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true)
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {isMobile && <TouchableOpacity onPress={handleBack}>
          <Icon name="arrowBack" size={25} color={theme.colors.black} />
        </TouchableOpacity>}
        <Text style={styles.headerTitle}>{isMobile? relationalColor.name : t('relationalColorForm.editTitle')}</Text>
        <TouchableOpacity disabled onPress={showDeleteModal}>
          {/*<Icon name="trash" size={20} color={theme.colors.error} />*/}
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <View style={!isMobile && commonStyles.row}>
          <View style={[styles.nameContainer, !isAndroid && {paddingVertical: theme.spacing.md}]}>
            <View style={[styles.color, {backgroundColor: relationalColor?.color}]}/>
            {/* Nom du relationalColor */}
            <TextInput
              style={styles.nameInput}
              placeholder={t('relationalColorForm.namePlaceholder')}
              value={name}
              onChangeText={setName}
              placeholderTextColor={theme.colors.greyBlue}
              autoCapitalize='none'
            />
            <Icon name="editPen" size={20} color={theme.colors.greyBlue} />
          </View>
        </View>
        <ContactsListEdit isEdit={relationalColor.contactAccountIds.length > 0} selectedContactIds={selectedContactIds} setSelectedContactIds={setSelectedContactIds} />
      </View>

      {/* Bouton Sauvegarder */}
      <View style={[styles.footer, !isMobile && commonStyles.rowBetween]}>
        {!isMobile && <CloseButton onPress={handleBack} />}
        <TouchableOpacity
          style={[styles.saveButton, (!name.trim() || !hasModified) && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!name.trim() || !hasModified}
        >
          <Text style={styles.saveButtonText}>
            {t('common.save')}
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const getStyle = (theme: Theme) =>
  StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  placeholder: {
    width: 30,
  },
  headerTitle: {
    fontSize: theme.fontSize.large,
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  deleteButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  color: {
    width: 25,
    height: 25,
    borderRadius: 13
  },
  nameContainer: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 25,
    paddingHorizontal: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: theme.colors.lightGrey,
  },
  nameInput: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  selectedContactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  selectedContactAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    marginRight: 12,
  },
  selectedContactName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  removeButton: {
    padding: 4,
  },
  footer: {
    padding: 16,
    backgroundColor: theme.colors.white,
  },
  saveButton: {
    borderRadius: 25,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xxxl,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: theme.fontSize.small,
    color: theme.colors.primary,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    minHeight: 200,
    maxHeight: '80%',
  },
  handle: {
    width: 36,
    height: 4,
    backgroundColor: theme.colors.lightGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  modalHeader: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.lightGrey,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colors.text,
    textAlign: 'center',
  },
  modalContactsList: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.lightGrey,
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  contactName: {
    flex: 1,
    fontSize: 16,
    color: theme.colors.text,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: theme.colors.lightGrey,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
  },
  checkboxSelected: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  selectionBar: {
    height: 40,
    width: 6,
    borderRadius: 3,
    backgroundColor: 'transparent',
    marginLeft: theme.spacing.md,
  },
  selectionBarActive: {
    backgroundColor: theme.colors.primary,
  },
  overlayTouchable: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  modalFooter: {
    padding: 20,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.lightGrey,
  },
  modalSaveButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 25,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalSaveButtonText: {
    fontSize: 17,
    fontWeight: '600',
    color: theme.colors.white,
  },
  safeArea: {
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: '#8E8E93',
  },
});

export default EditRelationalColor; 